import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Box, Group, Progress, Switch, Text, Tooltip } from "@mantine/core"
import type { IProps } from "./interfaces"
import { MIC_HIGH_THRESHOLD, MIC_LOW_THRESHOLD } from "./interfaces"
import classes from "./styles.module.css"

/**
 * Mic-level meter — raw + optional boost progress bar with dB readouts and
 * the sustained-condition alert slot below them. Pure presentation: every
 * piece of state (volume, boost, alert classification) flows in through
 * props from the smart wrapper, which owns the audio pipeline and the
 * hysteresis timers that decide when to surface an alert.
 */
export const MicLevelMeter = ({
  state,
  volume,
  boostedVolume,
  boostEnabled,
  micAlert,
  onBoostToggle,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()

  const volumePercent = Math.min(volume * 200, 100)
  const boostedVolumePercent = Math.min(boostedVolume * 200, 100)
  const rawDb = volume > 0.001 ? Math.round(20 * Math.log10(volume)) : null
  const boostedDb = boostedVolume > 0.001 ? Math.round(20 * Math.log10(boostedVolume)) : null

  const rawDbTone = volume > MIC_HIGH_THRESHOLD ? "warning" : volume < MIC_LOW_THRESHOLD ? "danger" : "success"
  const boostedDbTone = boostedVolume > MIC_HIGH_THRESHOLD ? "warning" : "success"
  const rawBarColor = volume > MIC_HIGH_THRESHOLD ? "warning" : "gray"
  const boostedBarColor = boostedVolume > MIC_HIGH_THRESHOLD ? "warning" : "info"

  return (
    <>
      <Group justify="space-between" mb={4}>
        <Group gap={6}>
          <Text size="xs" c="dimmed">
            Mic
          </Text>
          {rawDb !== null && (
            <Tooltip
              label={i18n._(
                msg`Sweet spot: −22 to −18 dB. Below −25 dB is too quiet; above −12 dB risks noise/distortion.`,
              )}
              withArrow
              position="top"
              multiline
              w={260}
            >
              <Text size="xs" c={rawDbTone} className={classes.dbReading}>
                {rawDb} dB
              </Text>
            </Tooltip>
          )}
        </Group>
        <Group gap="sm">
          <Switch
            size="xs"
            label={
              <Text size="xs" c="dimmed">
                Boost
              </Text>
            }
            checked={boostEnabled}
            onChange={(e) => onBoostToggle(e.currentTarget.checked)}
          />
        </Group>
      </Group>
      <Progress
        value={state === "paused" ? 0 : volumePercent}
        color={rawBarColor}
        size="sm"
        mb={boostEnabled ? 4 : "xs"}
        animated={state === "recording"}
      />
      {boostEnabled && (
        <>
          <Group gap={6} mb={4}>
            <Text size="xs" c="dimmed">
              Boosted
            </Text>
            {boostedDb !== null && (
              <Tooltip
                label={i18n._(msg`Boosted level after compressor and gain. Sweet spot: −22 to −18 dB.`)}
                withArrow
                position="top"
                multiline
                w={220}
              >
                <Text size="xs" c={boostedDbTone} className={classes.dbReading}>
                  {boostedDb} dB
                </Text>
              </Tooltip>
            )}
          </Group>
          <Progress
            value={state === "paused" ? 0 : boostedVolumePercent}
            color={boostedBarColor}
            size="sm"
            mb="xs"
            animated={state === "recording" || state === "idle"}
          />
        </>
      )}
      {/* Reserved space prevents layout shift when alert appears/disappears */}
      <Box className={classes.micAlertSlot} data-has-alert={micAlert ? "true" : "false"}>
        {micAlert === "auto-boosted" && (
          <Alert color="success" variant="light" py={6} px="sm" mb="xs">
            <Text size="xs">
              <Trans>Boost was automatically enabled — mic level was too low.</Trans>
            </Text>
          </Alert>
        )}
        {micAlert === "still-low" && (
          <Alert color="warning" variant="light" py={6} px="sm" mb="xs">
            <Text size="xs">
              <Trans>
                Mic level is still low even with Boost on. Go to <strong>System Settings → Sound → Input</strong> and
                raise the input volume.
              </Trans>
            </Text>
          </Alert>
        )}
        {micAlert === "clipping" && (
          <Alert color="warning" variant="light" py={6} px="sm" mb="xs">
            <Text size="xs">
              <Trans>Mic level is very high — audio may distort. Lower the input volume in System Settings.</Trans>
            </Text>
          </Alert>
        )}
      </Box>
    </>
  )
}
